import { useLazyQuery } from '@apollo/client'
import classNames from 'classnames'
import moment from 'moment'
import React, { memo, useEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import Moment from 'react-moment'

import axiosInstance from 'network/axiosClient'

import IconApprove from 'assets/images/icon-approve.svg'
import IconDot from 'assets/images/icon-dot.svg'
import IconViewEmailByAMS from 'assets/images/icon-email.svg'

import { isAxiosError } from 'axios'
import { useOutsideClick } from 'components/hooks/useOutsideClick'
import Loading from 'components/loading'
import ButtonBase from 'components/partials/button'
import SingleRowActionBase from 'components/partials/singleRowActionBase'
import { notify } from 'components/toastify'
import { styleJsonPacket } from 'constants/common'
import { MESSAGES, STATUS } from 'constants/messages'
import DateFilterPicker from 'pages/developer-portal/table/activities-log/component/dateFilterPicker'
import ViewDetailContentItemDialog from 'pages/email-system-management/components/view-detail-content-log'
import SearchInput from 'pages/principal-account-management/components/searchInput'
import { GET_RECEIVED_EMAIL_HISTORY_QUERY } from 'queries/adminPortal'
import { IDataSearchInput, IMessageMailItem, IReceivedEmailFilterItem } from '../../interface'
import GenerateJobDialog from './generate-job-dialog'
import { tableHeaderReceivedEmailHistoryTable } from './headers'
import TotalNumberInTable from 'components/partials/totalNumberInTable'

const PAGINATION_DEFAULT = {
  take: 20,
  skip: 0,
}
interface Props {
  setIsRefetchList: (refetchUser: boolean) => void
  isRefetchList: boolean
}

const ReceivedEmailHistoryTable: React.FC<Props> = () => {
  const [hasMoreEmailData, setHasMoreEmailData] = useState<boolean>(true)
  const [isOpenModalViewDetailEmail, setIsOpenModalViewDetailEmail] = useState<boolean>(false)
  const [isOpenModalGenerateJob, setIsOpenModalGenerateJob] = useState<boolean>(false)
  const [currentMessageMailItemSelected, setCurrentMessageMailItemSelected] =
    useState<IMessageMailItem>()
  const [listMessageMail, setListMessageMail] = useState<IMessageMailItem[]>([])
  const [searchObject, setSearchObject] = useState<IReceivedEmailFilterItem>({
    searchValue: '',
    skip: listMessageMail.length,
    take: PAGINATION_DEFAULT.take,
  })
  const prevSearchObjectRef = useRef({})
  const [disableDownloadBtn, setDisableDownloadBtn] = useState<boolean>(false)
  const [minDateFilter, setMinDateFilter] = useState<Date>()
  const [maxDateFilter, setMaxDateFilter] = useState<Date>()
  const [isFocusSearchJobIdOrEmail, setIsFocusSearchJobIdOrEmail] = useState<boolean>(false)
  const [isShowAction, setIsShowAction] = useState<boolean>(false)
  const [dataSearchInput, setDataSearchInput] = useState<IDataSearchInput>()
  const [totalCount, setTotalCount] = useState<number>(0)

  const [
    getReceivedEmailHistory,
    {
      loading: getReceivedEmailHistoryLoading,
      refetch: getReceivedEmailHistoryRefetch,
      fetchMore: getReceivedEmailHistoryFetchMore,
    },
  ] = useLazyQuery(GET_RECEIVED_EMAIL_HISTORY_QUERY)

  const handleGetData = async (searchObject: IReceivedEmailFilterItem) => {
    try {
      const response = await getReceivedEmailHistory({
        variables: searchObject,
        fetchPolicy: 'network-only',
      })
      if (response.data.getReceivedEmailData) {
        const { data, total } = response.data.getReceivedEmailData
        setTotalCount(total)
        setListMessageMail(data)
      }
    } catch (error) {
      notify(MESSAGES.ERROR.E_TRY_AGAIN, STATUS.ERROR)
    }
  }

  const handleRefetchEmailTable = async () => {
    const response = await getReceivedEmailHistoryRefetch(searchObject)
    if (response.data.getReceivedEmailData) {
      const { data, total } = response.data.getReceivedEmailData
      setListMessageMail(data)
      setTotalCount(total)
    }
  }

  const fetchMoreDataForMessageEmailHistory = async () => {
    try {
      const variables = {
        ...searchObject,
        skip: listMessageMail.length,
        take: PAGINATION_DEFAULT.take,
      }
      const response = await getReceivedEmailHistoryFetchMore({ variables })
      if (!response.data.getReceivedEmailData) return

      const { data } = response.data.getReceivedEmailData
      if (data.length > 0) {
        setHasMoreEmailData(data.length === PAGINATION_DEFAULT.take)
        const dataState = [...listMessageMail, ...data]
        setTimeout(() => {
          setListMessageMail(dataState)
        }, 1000)
      } else {
        setHasMoreEmailData(false)
        return
      }
    } catch (error: any) {
      notify(error.message ? error.message : MESSAGES.ERROR.E_TRY_AGAIN, STATUS.ERROR)
    }
  }

  const handleCloseEmailInfoDialog = () => {
    setIsOpenModalViewDetailEmail(false)
  }

  const onHandleClickViewDetailEmailAction = () => {
    setIsOpenModalViewDetailEmail(true)
  }

  const handleCloseGenerateJobDialog = () => {
    setIsOpenModalGenerateJob(false)
  }

  const onClickGenerateJobBtn = () => {
    setIsOpenModalGenerateJob(true)
  }

  const onClickDownloadCSVBtn = async () => {
    try {
      setDisableDownloadBtn(true)
      const response = await axiosInstance.post('/export/received/email', searchObject, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data])

      // Create a temporary URL for the Blob
      const url = URL.createObjectURL(blob)

      // Create a download link and initiate the file download
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = 'receivedEmail.csv'
      downloadLink.click()
    } catch (error: Error | any) {
      if (isAxiosError(error)) {
        notify(error.message, STATUS.ERROR)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    } finally {
      setDisableDownloadBtn(false)
    }
  }

  const startDateChange = (value: Date) => {
    setMinDateFilter(value)
    const dateFrom = value ? moment(value).format('YYYY-MM-DD') : ''
    setSearchObject((state) => {
      return { ...state, dateFrom, ...PAGINATION_DEFAULT }
    })
  }
  const endDateChange = (value: Date) => {
    setMaxDateFilter(value)
    const dateTo = value ? moment(value).format('YYYY-MM-DD') : ''
    setSearchObject((state) => {
      return { ...state, dateTo, ...PAGINATION_DEFAULT }
    })
  }
  const handleSearchBox = (value: string) => {
    setSearchObject((state) => {
      return { ...state, searchValue: value.trim(), ...PAGINATION_DEFAULT }
    })
  }

  useEffect(() => {
    if (
      searchObject &&
      JSON.stringify(searchObject) !== JSON.stringify(prevSearchObjectRef.current)
    ) {
      setHasMoreEmailData(true)
      handleGetData(searchObject)
    }
    prevSearchObjectRef.current = searchObject
  }, [searchObject])

  const onHandleClickShowAction = (messageMail: IMessageMailItem) => {
    if (currentMessageMailItemSelected?.messageId === messageMail.messageId) {
      setIsShowAction(!isShowAction)
    } else {
      setIsShowAction(true)
    }
    setCurrentMessageMailItemSelected(messageMail)
  }

  const onHandleGenerateJobAction = (messageMail: IMessageMailItem) => {
    setDataSearchInput({
      applicationNumber: messageMail.applicationNumber,
      carrierBondNumber: messageMail.carrierBondNumber,
    })

    setIsOpenModalGenerateJob(true)

    setIsShowAction(false)
  }

  const ref = useOutsideClick(() => {
    setIsShowAction(false)
  })

  return (
    <div>
      <div className="flex justify-between mb-3">
        <p className="pt-2 text-headline5A text-neutral-1">Received Email History</p>
        <div className="flex items-end space-x-2">
          <TotalNumberInTable number={totalCount} />
          <ButtonBase
            className="disabled:bg-primary-shade3"
            bgHover="Download detail email job csv"
            onClick={onClickDownloadCSVBtn}
            disabled={disableDownloadBtn}
          >
            Download CSV
          </ButtonBase>
          <ButtonBase
            className="disabled:bg-primary-shade3"
            bgHover="Download detail email job csv"
            onClick={onClickGenerateJobBtn}
          >
            Generate Job
          </ButtonBase>
        </div>
      </div>
      <div className="flex w-full mb-5">
        <DateFilterPicker
          className="w-1/4 pr-2"
          setCallBackDateChange={startDateChange}
          maxDate={maxDateFilter}
          defaultDate={moment().subtract(1, 'month').toDate()}
        />
        <DateFilterPicker
          className="w-1/4 pr-2"
          setCallBackDateChange={endDateChange}
          minDate={minDateFilter}
        />
        <div
          className={classNames('w-2/4 rounded-xl border-[2px]', {
            'border-neutral-3': isFocusSearchJobIdOrEmail,
            'border-neutral-5': !isFocusSearchJobIdOrEmail,
          })}
        >
          <SearchInput
            classInput="border-0 w-full"
            setIsFocus={setIsFocusSearchJobIdOrEmail}
            handleSearch={handleSearchBox}
            placehoderText="Search by PG Number or Bond Number or Subject"
            isHandleSearch={false}
          />
        </div>
      </div>
      <table className="w-full border-2 border-neutral-6 rounded-t-lg overflow-hidden">
        <thead className="bg-primary-shade4 text-body1 text-neutral-2">
          <tr className="w-full">
            {tableHeaderReceivedEmailHistoryTable.map((item: any, idx) => (
              <th
                key={idx}
                className={`font-semibold py-5 ${
                  idx === tableHeaderReceivedEmailHistoryTable.length - 1 ? `` : ` pl-2`
                } ${item.headerStyles}`}
              >
                <div className={`flex`}>
                  <span className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}>{item.name}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>
      {getReceivedEmailHistoryLoading && (
        <Loading className="relative py-6" height={30} width={30} />
      )}

      {listMessageMail?.length === 0 && !getReceivedEmailHistoryLoading ? (
        <p className="py-6 text-center text-body1 text-neutral-4">No data available.</p>
      ) : (
        Boolean(
          listMessageMail !== null &&
            listMessageMail?.length > 0 &&
            !getReceivedEmailHistoryLoading,
        ) && (
          <InfiniteScroll
            dataLength={listMessageMail?.length}
            next={fetchMoreDataForMessageEmailHistory}
            hasMore={hasMoreEmailData}
            height={'max-content'}
            style={{ maxHeight: 500 }}
            loader={''}
            endMessage={''}
            className="cus-scrollbar"
          >
            <table key={`table-key`} className="cursor-pointer w-full">
              {listMessageMail?.map((messageMail: IMessageMailItem, index: number) => (
                <tbody key={`table-job-${index}`}>
                  <tr className="h-[72px] w-full hover:bg-neutral-7 pt-5">
                    <td className={` ${tableHeaderReceivedEmailHistoryTable[0].rowStyles}`}>
                      <span>{messageMail.applicationNumber}</span>
                    </td>
                    <td className={`${tableHeaderReceivedEmailHistoryTable[1].rowStyles}`}>
                      <span>
                        {
                          <Moment format="yyyy-MM-DD HH:mm">
                            {moment.utc(messageMail.createdAt ?? '')}
                          </Moment>
                        }
                      </span>
                    </td>

                    <td className={`${tableHeaderReceivedEmailHistoryTable[2].rowStyles}`}>
                      <span> {messageMail?.receiver}</span>
                    </td>
                    <td className={`${tableHeaderReceivedEmailHistoryTable[3].rowStyles}`}>
                      <span>{messageMail.subject}</span>
                    </td>
                    <td className={`${tableHeaderReceivedEmailHistoryTable[4].rowStyles}`}>
                      <div className="flex w-full">
                        <p
                          dangerouslySetInnerHTML={{ __html: messageMail.content }}
                          style={{ ...styleJsonPacket }}
                          className={`break-all w-[85%]`}
                        ></p>
                        <div className="w-[15%] flex justify-center">
                          <button
                            title="View Email"
                            onClick={(e) => {
                              e.stopPropagation()
                              onHandleClickViewDetailEmailAction()
                              setCurrentMessageMailItemSelected(messageMail)
                            }}
                          >
                            <img src={IconViewEmailByAMS} alt="Icon-Hover-Copy" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className={`${tableHeaderReceivedEmailHistoryTable[5].rowStyles}`}>
                      <span>{messageMail.carrierBondNumber}</span>
                    </td>
                    <td className={`${tableHeaderReceivedEmailHistoryTable[6].rowStyles}`}>
                      <span
                        className={`${
                          messageMail?.isProcessed ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {' '}
                        {messageMail?.isProcessed?.toString().toUpperCase()}
                      </span>
                    </td>
                    <td className={`${tableHeaderReceivedEmailHistoryTable[7].rowStyles}`}>
                      <button
                        className="min-w-[24px]  mb-auto"
                        onClick={() => onHandleClickShowAction(messageMail)}
                      >
                        <div className="relative">
                          <img src={IconDot} alt="Icon-Dot" className="" />
                          {currentMessageMailItemSelected?.messageId === messageMail.messageId &&
                            isShowAction && (
                              <div
                                className={`absolute right-[2px] h-auto p-3 border shadow-2xl w-[220px] border-neutral-7 bg-neutral-8 rounded-xl z-10`}
                                ref={ref}
                              >
                                <SingleRowActionBase
                                  handleAction={() => onHandleGenerateJobAction(messageMail)}
                                  icon={IconApprove}
                                  action="GenerateJob"
                                  txt="Generate Job(s)"
                                />
                              </div>
                            )}
                        </div>
                      </button>
                    </td>
                  </tr>
                </tbody>
              ))}
            </table>
          </InfiniteScroll>
        )
      )}

      {isOpenModalViewDetailEmail && (
        <ViewDetailContentItemDialog
          key={currentMessageMailItemSelected?.messageId}
          modalIsOpen={true}
          title="View Email Detail"
          handleCloseDialog={handleCloseEmailInfoDialog}
          contentHtml={currentMessageMailItemSelected?.content || ''}
        />
      )}

      {isOpenModalGenerateJob && (
        <GenerateJobDialog
          modalIsOpen={isOpenModalGenerateJob}
          handleCloseDialog={handleCloseGenerateJobDialog}
          refetchEmailTable={handleRefetchEmailTable}
          initDataSearchInput={dataSearchInput}
        />
      )}
    </div>
  )
}
export default memo(ReceivedEmailHistoryTable)
